<template>
    <v-container fluid class="pa-0">
        <v-row>
            <v-col>
                <v-card style="margin-bottom: 2px">
                    <v-card-title> Experiments </v-card-title>
                </v-card>
                <filter-panel
                    :filterOptions="filterOptions"
                    :model-value="filterState"
                    @update:modelValue="onUpdateFilters"
                />
                <v-data-table
                    density="compact"
                    hover
                    sticky
                    :headers="experimentHeaders"
                    :items="filteredExperiments"
                    :search="search"
                    no-results-text="No matching records found"
                    item-key="code"
                    :items-per-page="15"
                    :loading="loading"
                    @click:row="link"
                >
                    <template v-slot:loading>
                        <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
                    </template>

                    <template v-slot:item.day0="{ item }">
                        {{ formatDate(item.day0, item.siteTimeZone) }}
                    </template>
                    <template v-slot:item.status="{ item }">
                        <v-chip
                            v-if="item.status"
                            :color="getStatusColor(item.status)"
                            size="small"
                        >
                            {{ item.status }}
                        </v-chip>
                    </template>
                    <template v-slot:item.actions="{ item }">
                        <v-icon-btn
                            v-if="userCanManageV2Metadata"
                            density="compact"
                            color="primary"
                            @click.stop="openEditMetadataDialog(item)"
                        >
                            <v-icon>$pencil</v-icon>
                        </v-icon-btn>
                        <v-icon-btn
                            density="compact"
                            color="error"
                            v-if="userCanDeleteExperiments"
                            @click.stop="
                                experimentToDelete = item;
                                confirmDeleteDialog = true;
                            "
                        >
                            <v-icon>$trash-can-outline</v-icon>
                        </v-icon-btn>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
        <v-dialog v-if="experimentToDelete" v-model="confirmDeleteDialog" max-width="290">
            <v-card>
                <v-card-title class="text-h5"> Delete {{ experimentToDelete.code }} </v-card-title>
                <v-card-text>
                    Are you sure you want to delete {{ experimentToDelete.code }}?
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="green-darken-1"
                        variant="text"
                        @click="confirmDeleteDialog = false"
                    >
                        No
                    </v-btn>
                    <v-btn
                        color="green-darken-1"
                        :loading="isDeleting"
                        :disabled="isDeleting"
                        variant="text"
                        @click.stop="deleteExperiment(experimentToDelete)"
                    >
                        Yes
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="showEditMetadataDialog" max-width="80%">
            <experiment-metadata-form
                :experiment="experimentToEdit!"
                @metadataUpdated="handleExperimentMetadataSaved"
                @cancel="closeEditMetadataDialog"
                @close="closeEditMetadataDialog"
            />
        </v-dialog>
    </v-container>
</template>

<script setup lang="ts">

import { ref, onMounted, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from 'vuex'
import axios from "../../lib/apiAxios";
import { archiveExperiment } from "@/services/api.services";
import { getISODateOnlyStringInSiteTimeZone } from "@/lib/dateUtils";
import FilterPanel from "@/components/common/FilterPanel.vue";
import { buildFilterOptionsFromData, filterData } from "@/lib/filterUtils";
import ExperimentMetadataForm from "@/components/Experiments/ExperimentMetadataForm.vue";
import { VDataTableHeader } from "@/vuetifyTypeFixes";
import { useAuth } from "@/lib/ts/auth";
import { ExperimentListDTO } from "@/services/experiments/experimentsAPI";

const auth = useAuth();
const router = useRouter();
const store = useStore();
const experiments = ref<ExperimentListDTO[]>([]);
const loading = ref(false);
const confirmDeleteDialog = ref(false);
const showEditMetadataDialog = ref(false);
const showLegacyEditMetadataDialog = ref(false);
const isDeleting = ref(false);
const experimentToEdit = ref<ExperimentListDTO | null>(null);
const experimentToDelete = ref<ExperimentListDTO | null>(null);
const filterOptions = ref({});
const filterState = ref(<Record<string, string[]>>{});
const filterConfig = ref([
    { name: "experimentGroup", prop: "experimentGroupName" },
    { name: "schemaConfigCode", prop: "schemaConfigCode" },
    { name: "schemaConfig", prop: "schemaConfigName" },
    { name: "investigator", prop: "investigator" },
    { name: "operator", prop: "operator", multiple: true },
    { name: "studyDirector", prop: "studyDirector" },
    { name: "siteCountry", prop: "siteCountry" },
    { name: "invaioProject", prop: "invaioProject" },
    { name: "experimentStatus", prop: "status", noneOption: true },
]);

const experimentHeaders = ref<VDataTableHeader[]>([
    {
        title: "Exp #",
        value: "code",
        sortable: true,
        width: "5%",
    },
    {
        title: "Experiment Name",
        value: "name",
        align: "start",
        sortable: true,
        width: "23%",
    },
    {
        title: "Exp Group Code",
        value: "experimentGroupCode",
        align: "start",
        sortable: true,
        width: "5%",
    },
    {
        title: "Exp Group Name",
        value: "experimentGroupName",
        align: "start",
        sortable: true,
        width: "22%",
    },
    {
        title: "Day 0",
        value: "day0",
        align: "start",
        sortable: true,
        width: "10%",
    },
    {
        title: "Investigator",
        value: "investigator",
        align: "start",
        sortable: true,
        width: "8%",
    },
    {
        title: "Site",
        value: "siteName",
        align: "start",
        sortable: true,
        width: "15%",
    },
    {
        title: "Status",
        value: "status",
        align: "start",
        sortable: true,
        width: "5%",
    },
    {
        title: "Actions",
        value: "actions",
        align: "start",
        width: "8%",
    },
]);

const emit = defineEmits<{
    (e: "updateFilters", newFilters: Record<string, string[]>): void;
    (e: "globalAlert", message: string): void;
}>();

onMounted(async () => {
    await load();
});

const search = computed(() => store.state.globalSearch);

async function load() {
    loading.value = true; // replace with withLoadingScreen?
    try {
        const response = await axios.get(`/experiments`);
        experiments.value = response.data || [];
    } catch (error) {
        console.error("Error loading experiments:", error);
    } finally {
        loading.value = false;
    }
    filterOptions.value = buildFilterOptionsFromData(experiments.value, filterConfig.value);
}

async function deleteExperiment(item: ExperimentListDTO) {
    try {
        isDeleting.value = true;
        await archiveExperiment(item.code);
        emit("globalAlert", `${item.code} was successfully deleted.`);
    } catch (err) {
        console.error(err);
        emit("globalAlert", `An error occurred while deleting ${item.code}.`);
    } finally {
        isDeleting.value = false;
        confirmDeleteDialog.value = false;
        await load();
    }
}

function formatDate(date: String, timeZone: String) { // TODO: double-check date param type
    return getISODateOnlyStringInSiteTimeZone(date, timeZone);
}

function onUpdateFilters(newFilters: Record<string, string[]>) {
    filterState.value = newFilters;
}

function getStatusColor(status: string) {
    switch (status) {
        case "Ongoing":
            return "blue";
        case "Not started":
            return "grey";
        case "Completed":
            return "green";
        case "Canceled":
            return "darkgrey";
        case "Blocked":
            return "red";
        default:
            return "";
    }
}

function closeEditMetadataDialog() {
    showEditMetadataDialog.value = false;
    showLegacyEditMetadataDialog.value = false;
}

async function handleExperimentMetadataSaved() {
    closeEditMetadataDialog();
    await load();
}

async function openEditMetadataDialog(experiment: ExperimentListDTO) {
    experimentToEdit.value = experiment;
    showEditMetadataDialog.value = true;
}

const filteredExperiments = computed(() => {
    return filterData(experiments.value, filterState.value, filterConfig.value) as ExperimentListDTO[];
});

watch(
    () => experiments,
    async (newExperiments) => {
        if (!experiments.value.length) return;
        filterOptions.value = buildFilterOptionsFromData(newExperiments, filterConfig.value);
    },
    { immediate: true }
);

const userCanDeleteExperiments = computed(() => {
    return auth.isAuthorized("Experiments:Delete");
});

const userCanManageV2Metadata = computed(() => {
    return auth.isAuthorized("Experiments:ManageV2Metadata");
});

function link(_event, { item }) {
    if (!item.protocolId) {
        router.push({ path: `/experiment/${item.code}` });
    } else {
        router.push({ path: `/experiment/${item.code}/${item.protocolId}` });
    }
}
</script>
